/**
 * AdminConversions — focused dashboard for the post-2026-05-22 conversion
 * bundle. Separate from /admin/funnel which covers the full lead→Pro flow;
 * this page zooms in on the four levers that drive WEEK-OVER-WEEK A/B
 * decisions:
 *
 *   1. Score-upsell modal — impressions, CTR, dismisses by reason × tone
 *   2. Bottom-CTA copy A/B (3 variants) — variant CTR + verdict
 *   3. Free-ATS page funnel — page_view → submit drop-off
 *   4. Score-band distribution of recent checks
 *
 * Auto-polls /api/admin/conversions/live every 30s so the operator can
 * leave it open during a copy iteration and see lift in real time.
 * 24h / 7d / 30d window toggle.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Navigate } from 'react-router-dom';
import {
  AlertTriangle, Award, Clock, Eye, Inbox, Loader2, Mail, MousePointer,
  RefreshCw, TrendingDown, TrendingUp, X,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { API } from '../utils/apiBase';
import useHideCookieBanner from '../utils/useHideCookieBanner';
import AdminReadinessBanner from '../components/AdminReadinessBanner';
import AdminSubNav from '../components/AdminSubNav';

const REFRESH_MS = 30_000;

const WINDOW_OPTIONS = [
  { label: '24h', hours: 24 },
  { label: '7d', hours: 24 * 7 },
  { label: '30d', hours: 24 * 30 },
];

// ── Sub-components ──────────────────────────────────────────────────────
const StatTile = ({ icon: Icon, label, value, sub, tone = 'neutral', testId }) => {
  const toneColor =
    tone === 'good' ? '#10b981' : tone === 'bad' ? '#e11d48' : tone === 'warn' ? '#f59e0b' : '#0f1115';
  return (
    <div className="bg-white border border-[#e5e7eb] p-5" data-testid={testId}>
      <div className="flex items-center gap-2 text-[#6b7280] text-[10px] uppercase tracking-[0.15em] mb-2 font-semibold">
        {Icon && <Icon size={12} />}
        <span>{label}</span>
      </div>
      <div className="font-satoshi text-3xl font-black" style={{ color: toneColor }}>{value}</div>
      {sub && <div className="text-xs text-[#6b7280] mt-1">{sub}</div>}
    </div>
  );
};

// Horizontal stacked bar showing dismiss-reason composition.
const ReasonBar = ({ reasons, total, testId }) => {
  if (!reasons || total === 0) {
    return <div className="text-xs text-[#9ca3af] mt-2" data-testid={`${testId}-empty`}>No dismisses in window.</div>;
  }
  const order = [
    { key: 'maybe_later', color: '#dc2626', label: 'Maybe later' },
    { key: 'close_x', color: '#f59e0b', label: 'Close (X)' },
    { key: 'backdrop', color: '#9ca3af', label: 'Backdrop' },
  ];
  return (
    <div className="mt-3" data-testid={testId}>
      <div className="flex h-2.5 overflow-hidden bg-[#f4f5f7]">
        {order.map(({ key, color }) => {
          const n = reasons[key]?.count || 0;
          const pct = (n / total) * 100;
          if (pct === 0) return null;
          return (
            <div
              key={key}
              style={{ width: `${pct}%`, backgroundColor: color }}
              title={`${key}: ${n} (${pct.toFixed(1)}%)`}
            />
          );
        })}
      </div>
      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-[#4b5563]">
        {order.map(({ key, color, label }) => {
          const n = reasons[key]?.count || 0;
          const pct = reasons[key]?.pct_of_dismisses || 0;
          return (
            <span key={key} className="inline-flex items-center gap-1" data-testid={`${testId}-${key}`}>
              <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
              <span className="font-semibold">{label}</span>
              <span className="text-[#9ca3af]">{n} · {pct}%</span>
            </span>
          );
        })}
      </div>
    </div>
  );
};

const TonePanel = ({ title, band, accent, testId }) => {
  if (!band) return null;
  const ctr = band.cta_click_rate_pct || 0;
  const isHealthy = ctr >= 8;
  return (
    <div className="bg-white border border-[#e5e7eb] p-5" data-testid={testId}>
      <div className="flex items-baseline justify-between mb-2">
        <h3 className="font-satoshi text-sm font-bold text-[#0f1115]">
          <span className="inline-block w-2 h-2 rounded-full mr-2 align-middle" style={{ backgroundColor: accent }} />
          {title}
        </h3>
        <span
          className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 ${
            isHealthy ? 'bg-[#ecfdf5] text-[#065f46]' : 'bg-[#fef2f2] text-[#991b1b]'
          }`}
          data-testid={`${testId}-health`}
        >
          {isHealthy ? 'Healthy' : 'Needs lift'}
        </span>
      </div>
      <div className="grid grid-cols-3 gap-3 mt-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#9ca3af] mb-0.5">Impr.</div>
          <div className="font-satoshi text-xl font-black text-[#0f1115]" data-testid={`${testId}-impressions`}>
            {band.impressions.toLocaleString()}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#9ca3af] mb-0.5">CTR</div>
          <div className="font-satoshi text-xl font-black text-[#0f1115]" data-testid={`${testId}-ctr`}>
            {ctr}%
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#9ca3af] mb-0.5">Dismiss</div>
          <div className="font-satoshi text-xl font-black text-[#0f1115]" data-testid={`${testId}-dismiss-rate`}>
            {band.dismiss_rate_pct || 0}%
          </div>
        </div>
      </div>
      <ReasonBar reasons={band.dismiss_reasons} total={band.dismisses || 0} testId={`${testId}-reasons`} />
    </div>
  );
};

const AbVariantRow = ({ variant, data, isWinner }) => {
  const labels = {
    v_unlock_unlimited: 'Unlock unlimited',
    v_see_pricing: 'See pricing',
    v_get_pro_features: 'Get Pro features',
  };
  return (
    <tr
      className={`border-t border-[#e5e7eb] ${isWinner ? 'bg-[#ecfdf5]' : ''}`}
      data-testid={`ab-row-${variant}`}
    >
      <td className="py-2.5 px-3 text-sm font-semibold text-[#0f1115]">
        {labels[variant] || variant}
        {isWinner && (
          <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#065f46]">
            <Award size={11} /> Leader
          </span>
        )}
      </td>
      <td className="py-2.5 px-3 text-sm text-right tabular-nums">{(data?.impressions || 0).toLocaleString()}</td>
      <td className="py-2.5 px-3 text-sm text-right tabular-nums">{(data?.clicks || 0).toLocaleString()}</td>
      <td className="py-2.5 px-3 text-sm text-right font-bold tabular-nums" data-testid={`ab-ctr-${variant}`}>
        {data?.ctr_pct || 0}%
      </td>
    </tr>
  );
};

const FunnelStep = ({ step, visitors, pct, dropFromPrev, isLast }) => {
  // Convert backend step name to human label
  const labels = {
    page_view: 'Page view',
    scroll_25: '25% scroll',
    scroll_50: '50% scroll',
    scroll_75: '75% scroll',
    scroll_100: '100% scroll',
    first_interaction: 'Form focused',
    submit_attempted: 'Submit clicked',
    validation_passed: 'Validation passed',
    score_shown: 'Score shown',
    unlock_email_submitted: 'Email submitted',
    unlock_completed: 'Report unlocked',
    check_completed: 'Score generated',
  };
  return (
    <div className="relative" data-testid={`funnel-step-${step}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-semibold text-[#0f1115]">{labels[step] || step}</span>
        <div className="flex items-baseline gap-2">
          <span className="text-sm font-bold text-[#0f1115] tabular-nums">{visitors.toLocaleString()}</span>
          <span className="text-xs text-[#6b7280] tabular-nums">{pct}%</span>
          {dropFromPrev != null && dropFromPrev < 0 && (
            <span className="text-[10px] font-bold text-[#e11d48] tabular-nums">
              {dropFromPrev}%
            </span>
          )}
        </div>
      </div>
      <div className="h-6 bg-[#f4f5f7] relative overflow-hidden">
        <div
          className="h-full transition-all duration-700 ease-out"
          style={{
            width: `${Math.min(100, pct)}%`,
            background: isLast ? '#10b981' : '#002fa7',
          }}
        />
      </div>
    </div>
  );
};

const ScoreBandTile = ({ label, count, pct, color, testId }) => (
  <div className="flex-1 min-w-0" data-testid={testId}>
    <div className="text-[10px] uppercase tracking-wider text-[#9ca3af] font-semibold mb-1">{label}</div>
    <div className="flex items-baseline gap-1.5">
      <span className="font-satoshi text-2xl font-black text-[#0f1115]">{count.toLocaleString()}</span>
      <span className="text-xs text-[#6b7280]">{pct}%</span>
    </div>
    <div className="h-1.5 bg-[#f4f5f7] mt-1.5 overflow-hidden">
      <div className="h-full transition-all" style={{ width: `${Math.min(100, pct)}%`, backgroundColor: color }} />
    </div>
  </div>
);

// ── Main page ───────────────────────────────────────────────────────────
const AdminConversions = () => {
  const { user, loading: authLoading } = useAuth();
  useHideCookieBanner();
  const [hours, setHours] = useState(24);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);
  const intervalRef = useRef(null);

  const load = useCallback(async (showSpinner = false) => {
    if (showSpinner) setLoading(true);
    else setRefreshing(true);
    setErr('');
    try {
      const res = await axios.get(`${API}/admin/conversions/live`, {
        params: { hours },
        withCredentials: true,
      });
      setData(res.data);
      setLastUpdated(new Date());
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load conversions.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [hours]);

  useEffect(() => {
    load(true);
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => load(false), REFRESH_MS);
    return () => intervalRef.current && clearInterval(intervalRef.current);
  }, [load]);

  if (authLoading) return <div className="pt-24 text-center text-[#6b7280]">Loading…</div>;
  if (!user || user.role !== 'admin') return <Navigate to="/dashboard" replace />;

  const modal = data?.score_upsell_modal;
  const ab = data?.cta_ab_test;
  const funnel = data?.free_ats_page_funnel?.funnel || [];
  const blockReasons = data?.free_ats_page_funnel?.block_reasons || [];
  const recovery = data?.recovery_queue || null;
  const bands = data?.score_band_distribution;

  // Derive A/B winner if verdict declares one
  const winnerVariant = ab?.verdict?.winner;
  // Per-step drop-off vs previous step
  const funnelWithDrop = funnel.map((s, i) => {
    if (i === 0) return { ...s, dropFromPrev: null };
    const prev = funnel[i - 1].pct_of_page_view || 0;
    const drop = (s.pct_of_page_view || 0) - prev;
    return { ...s, dropFromPrev: Math.round(drop * 10) / 10 };
  });

  return (
    <div className="min-h-screen bg-[#f4f5f7] pt-20 pb-12" data-testid="admin-conversions-page">
      <AdminReadinessBanner />
      <AdminSubNav />
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#002fa7]">ADMIN · LIVE</span>
            <h1 className="font-satoshi text-3xl font-black text-[#0f1115]">Conversion Dashboard</h1>
            <p className="text-sm text-[#6b7280]">
              Upsell modal + CTA A/B + funnel drop-off · auto-refresh every 30s
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {WINDOW_OPTIONS.map((opt) => (
              <button
                key={opt.label}
                onClick={() => setHours(opt.hours)}
                className={`px-3 py-2 text-sm font-semibold transition-colors ${
                  hours === opt.hours
                    ? 'bg-[#002fa7] text-white'
                    : 'bg-white border border-[#e5e7eb] text-[#4b5563] hover:border-[#002fa7]'
                }`}
                data-testid={`window-${opt.label}`}
              >
                {opt.label}
              </button>
            ))}
            <button
              onClick={() => load(false)}
              disabled={refreshing}
              className="px-3 py-2 text-sm font-semibold bg-white border border-[#e5e7eb] text-[#4b5563] hover:border-[#002fa7] transition-colors inline-flex items-center gap-1.5 disabled:opacity-50"
              data-testid="manual-refresh"
              title="Refresh now"
            >
              <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
              {refreshing ? 'Updating' : 'Refresh'}
            </button>
          </div>
        </div>

        {/* Live indicator */}
        <div className="flex items-center gap-2 mb-6 text-xs text-[#6b7280]" data-testid="last-updated">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#10b981] opacity-60" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-[#10b981]" />
          </span>
          {lastUpdated
            ? `Last updated ${lastUpdated.toLocaleTimeString()} · refreshes every ${REFRESH_MS / 1000}s`
            : 'Waiting for first refresh…'}
        </div>

        {err && (
          <div
            className="mb-6 p-4 bg-red-50 border border-red-200 text-red-800 flex items-center gap-2"
            data-testid="conversions-error"
          >
            <AlertTriangle size={16} /> {err}
          </div>
        )}

        {loading && !data ? (
          <div className="flex items-center justify-center py-20 text-[#6b7280]">
            <Loader2 className="animate-spin mr-2" size={20} /> Loading conversions…
          </div>
        ) : data ? (
          <>
            {/* Headline tiles */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="headline-tiles">
              <StatTile
                icon={Eye}
                label="Upsell impressions"
                value={(modal?.total_impressions || 0).toLocaleString()}
                sub={`Score < 75 cohort`}
                testId="tile-upsell-impressions"
              />
              <StatTile
                icon={MousePointer}
                label="Upsell CTR"
                value={`${modal?.overall_ctr_pct || 0}%`}
                tone={(modal?.overall_ctr_pct || 0) >= 8 ? 'good' : (modal?.overall_ctr_pct || 0) >= 3 ? 'warn' : 'bad'}
                sub={`${(modal?.total_cta_clicks || 0).toLocaleString()} clicks → /pricing`}
                testId="tile-upsell-ctr"
              />
              <StatTile
                icon={TrendingDown}
                label="Page → Submit"
                value={`${funnel.find((s) => s.step === 'submit_attempted')?.pct_of_page_view || 0}%`}
                tone={(funnel.find((s) => s.step === 'submit_attempted')?.pct_of_page_view || 0) >= 20 ? 'good' : 'bad'}
                sub={`${funnel[0]?.visitors?.toLocaleString() || 0} page views`}
                testId="tile-funnel-submit"
              />
              <StatTile
                icon={Clock}
                label="A/B verdict"
                value={ab?.verdict?.status === 'winner' ? 'Winner' : ab?.verdict?.status === 'collecting' ? 'Collecting' : 'Inconclusive'}
                tone={ab?.verdict?.status === 'winner' ? 'good' : 'neutral'}
                sub={
                  ab?.verdict?.status === 'collecting' && ab?.verdict?.remaining
                    ? `${Math.max(...Object.values(ab.verdict.remaining))} more impr. needed`
                    : ab?.verdict?.status === 'winner'
                      ? `${ab.verdict.lift_pct}% lift`
                      : 'No significant diff'
                }
                testId="tile-ab-verdict"
              />
            </div>

            {/* Upsell Modal Panel — split by tone */}
            <section className="mb-8" data-testid="upsell-modal-section">
              <h2 className="font-satoshi text-lg font-bold text-[#0f1115] mb-3 flex items-center gap-2">
                <Award size={18} className="text-[#002fa7]" />
                Score-triggered Upsell Modal
                <span className="text-xs font-normal text-[#6b7280]">— "decide right here" lever for &lt;75 scores</span>
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                <TonePanel
                  title="Critical band · score < 55"
                  band={modal?.critical_band}
                  accent="#dc2626"
                  testId="panel-critical"
                />
                <TonePanel
                  title="Warning band · 55 – 74"
                  band={modal?.warning_band}
                  accent="#f59e0b"
                  testId="panel-warning"
                />
              </div>
            </section>

            {/* Bottom CTA A/B Panel */}
            <section className="mb-8" data-testid="ab-test-section">
              <h2 className="font-satoshi text-lg font-bold text-[#0f1115] mb-3 flex items-center gap-2">
                <TrendingUp size={18} className="text-[#002fa7]" />
                Bottom-CTA Copy A/B Test
                <span className="text-xs font-normal text-[#6b7280]">
                  — {ab?.verdict?.status === 'collecting' ? 'Collecting data…' : ab?.verdict?.status === 'winner' ? `${ab.verdict.winner} is winning` : 'No clear winner yet'}
                </span>
              </h2>
              <div className="bg-white border border-[#e5e7eb] overflow-hidden">
                <table className="w-full" data-testid="ab-test-table">
                  <thead className="bg-[#f9fafb]">
                    <tr className="text-[10px] uppercase tracking-wider text-[#6b7280]">
                      <th className="py-2.5 px-3 text-left font-semibold">Variant</th>
                      <th className="py-2.5 px-3 text-right font-semibold">Impressions</th>
                      <th className="py-2.5 px-3 text-right font-semibold">Clicks</th>
                      <th className="py-2.5 px-3 text-right font-semibold">CTR</th>
                    </tr>
                  </thead>
                  <tbody>
                    {['v_unlock_unlimited', 'v_see_pricing', 'v_get_pro_features'].map((v) => (
                      <AbVariantRow
                        key={v}
                        variant={v}
                        data={ab?.[v]}
                        isWinner={winnerVariant === v}
                      />
                    ))}
                  </tbody>
                </table>
                {ab?.verdict?.status === 'collecting' && ab?.verdict?.remaining && (
                  <div className="p-3 bg-[#fef3c7] border-t border-[#e5e7eb] text-xs text-[#7c2d12]" data-testid="ab-collecting-note">
                    Verdict requires ≥{ab.verdict.min_impressions_per_arm} impressions per arm and ≥{ab.verdict.min_lift_pct}% lift.
                    Remaining: {Object.entries(ab.verdict.remaining)
                      .map(([v, n]) => `${v.replace('v_', '')} +${n}`)
                      .join(' · ')}
                  </div>
                )}
              </div>
            </section>

            {/* Funnel drop-off */}
            <section className="mb-8" data-testid="funnel-section">
              <h2 className="font-satoshi text-lg font-bold text-[#0f1115] mb-3 flex items-center gap-2">
                <TrendingDown size={18} className="text-[#e11d48]" />
                /free-ats-check Page Funnel
                <span className="text-xs font-normal text-[#6b7280]">— where visitors drop off</span>
              </h2>
              <div className="bg-white border border-[#e5e7eb] p-5">
                {funnelWithDrop.length === 0 ? (
                  <p className="text-sm text-[#6b7280]" data-testid="funnel-empty">
                    No funnel events in this window yet. Waiting for traffic…
                  </p>
                ) : (
                  <div className="space-y-3">
                    {funnelWithDrop.map((s, i) => (
                      <FunnelStep
                        key={s.step}
                        step={s.step}
                        visitors={s.visitors || 0}
                        pct={s.pct_of_page_view || 0}
                        dropFromPrev={s.dropFromPrev}
                        isLast={i === funnelWithDrop.length - 1}
                      />
                    ))}
                  </div>
                )}
              </div>
            </section>

            {/* Block-reasons — WHY submit-attempted didn't progress to score-shown.
                Counts are raw events (not distinct visitors) so we see retries. */}
            <section className="mb-8" data-testid="block-reasons-section">
              <h2 className="font-satoshi text-lg font-bold text-[#0f1115] mb-3 flex items-center gap-2">
                <AlertTriangle size={18} className="text-[#f59e0b]" />
                Submit Drop-off — Loss Reasons
                <span className="text-xs font-normal text-[#6b7280]">— why people who tried to submit didn't get a score</span>
              </h2>
              <div className="bg-white border border-[#e5e7eb] p-5">
                {blockReasons.filter((r) => r.count > 0).length === 0 ? (
                  <p className="text-sm text-[#6b7280]" data-testid="block-reasons-empty">
                    No blocked submissions in this window — everyone who tried got through. (If you expect blocks here and see none, deploy the latest build and wait for traffic.)
                  </p>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {blockReasons.map((r) => {
                      const labels = {
                        blocked_no_resume: 'No resume entered',
                        blocked_short_resume: 'Resume < 50 chars',
                        blocked_no_target: 'No target job',
                        blocked_ai_down: 'AI scorer down',
                        api_error: 'Backend API error',
                        email_failed: 'Post-unlock email failed',
                      };
                      const tone = r.reason === 'blocked_ai_down' || r.reason === 'api_error' ? 'bad' : 'warn';
                      const toneColor = tone === 'bad' ? '#e11d48' : '#f59e0b';
                      return (
                        <div
                          key={r.reason}
                          className="border border-[#e5e7eb] p-3"
                          data-testid={`block-reason-${r.reason}`}
                        >
                          <div className="text-[10px] uppercase tracking-[0.15em] text-[#6b7280] font-semibold">
                            {labels[r.reason] || r.reason}
                          </div>
                          <div className="font-satoshi text-2xl font-black mt-1" style={{ color: toneColor }}>
                            {(r.count || 0).toLocaleString()}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </section>
            {/* Recovery Queue health — re-engagement worker stats */}
            {recovery && (
              <section className="mb-8" data-testid="recovery-queue-section">
                <h2 className="font-satoshi text-lg font-bold text-[#0f1115] mb-3 flex items-center gap-2">
                  <Mail size={18} className="text-[#002fa7]" />
                  Recovery Email Queue
                  <span className="text-xs font-normal text-[#6b7280]">— re-engaging blocked visitors we already know</span>
                </h2>
                <div className="bg-white border border-[#e5e7eb] p-5">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div data-testid="recovery-pending">
                      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#6b7280] font-semibold mb-1">
                        <Inbox size={11} /> Pending
                      </div>
                      <div className="font-satoshi text-2xl font-black text-[#0f1115]">
                        {(recovery.pending || 0).toLocaleString()}
                      </div>
                      <div className="text-[11px] text-[#6b7280] mt-0.5">queued for send</div>
                    </div>
                    <div data-testid="recovery-sent">
                      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#6b7280] font-semibold mb-1">
                        <Mail size={11} className="text-[#10b981]" /> Sent in window
                      </div>
                      <div className="font-satoshi text-2xl font-black text-[#10b981]">
                        {(recovery.sent_in_window || 0).toLocaleString()}
                      </div>
                      <div className="text-[11px] text-[#6b7280] mt-0.5">delivered by worker</div>
                    </div>
                    <div data-testid="recovery-skipped">
                      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#6b7280] font-semibold mb-1">
                        <Clock size={11} /> Skipped
                      </div>
                      <div className="font-satoshi text-2xl font-black text-[#6b7280]">
                        {(recovery.skipped_in_window || 0).toLocaleString()}
                      </div>
                      <div className="text-[11px] text-[#6b7280] mt-0.5">already paid / recovered</div>
                    </div>
                    <div data-testid="recovery-failed">
                      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#6b7280] font-semibold mb-1">
                        <AlertTriangle size={11} className={recovery.failed_in_window > 0 ? 'text-[#e11d48]' : 'text-[#9ca3af]'} />
                        Failed
                      </div>
                      <div className={`font-satoshi text-2xl font-black ${recovery.failed_in_window > 0 ? 'text-[#e11d48]' : 'text-[#9ca3af]'}`}>
                        {(recovery.failed_in_window || 0).toLocaleString()}
                      </div>
                      <div className="text-[11px] text-[#6b7280] mt-0.5">send errors</div>
                    </div>
                  </div>

                  {/* Worker-stall canary */}
                  {recovery.oldest_pending_age_min != null && (
                    <div
                      className={`mt-4 p-3 border-l-4 text-xs ${
                        recovery.oldest_pending_age_min > 75
                          ? 'bg-[#fef2f2] border-[#e11d48] text-[#7f1d1d]'
                          : 'bg-[#f0f9ff] border-[#0284c7] text-[#075985]'
                      }`}
                      data-testid="recovery-oldest-pending"
                    >
                      Oldest pending row: <strong>{recovery.oldest_pending_age_min} min</strong> old.
                      {recovery.oldest_pending_age_min > 75 && ' Worker may be stalled — check backend logs.'}
                    </div>
                  )}

                  {/* Per-reason breakdown */}
                  {Array.isArray(recovery.by_reason) && recovery.by_reason.length > 0 && (
                    <div className="mt-5">
                      <div className="text-[10px] uppercase tracking-wider text-[#6b7280] font-semibold mb-2">
                        Send outcomes by block reason (all-time)
                      </div>
                      <table className="w-full text-sm" data-testid="recovery-by-reason-table">
                        <thead>
                          <tr className="text-[10px] uppercase tracking-wider text-[#9ca3af]">
                            <th className="py-1.5 px-2 text-left font-semibold">Reason</th>
                            <th className="py-1.5 px-2 text-right font-semibold">Pending</th>
                            <th className="py-1.5 px-2 text-right font-semibold">Sent</th>
                            <th className="py-1.5 px-2 text-right font-semibold">Skipped</th>
                            <th className="py-1.5 px-2 text-right font-semibold">Failed</th>
                          </tr>
                        </thead>
                        <tbody>
                          {recovery.by_reason.map((r) => (
                            <tr key={r.reason} className="border-t border-[#e5e7eb]" data-testid={`recovery-reason-row-${r.reason}`}>
                              <td className="py-2 px-2 font-semibold text-[#0f1115]">{r.reason}</td>
                              <td className="py-2 px-2 text-right tabular-nums">{(r.pending || 0).toLocaleString()}</td>
                              <td className="py-2 px-2 text-right tabular-nums text-[#10b981] font-semibold">{(r.sent || 0).toLocaleString()}</td>
                              <td className="py-2 px-2 text-right tabular-nums text-[#6b7280]">{(r.skipped || 0).toLocaleString()}</td>
                              <td className={`py-2 px-2 text-right tabular-nums font-semibold ${(r.failed || 0) > 0 ? 'text-[#e11d48]' : 'text-[#9ca3af]'}`}>
                                {(r.failed || 0).toLocaleString()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </section>
            )}

            {bands && (
              <section className="mb-8" data-testid="score-bands-section">
                <h2 className="font-satoshi text-lg font-bold text-[#0f1115] mb-3 flex items-center gap-2">
                  <Award size={18} className="text-[#002fa7]" />
                  Score-band Distribution
                  <span className="text-xs font-normal text-[#6b7280]">
                    — {(bands.total || 0).toLocaleString()} checks in window
                  </span>
                </h2>
                <div className="bg-white border border-[#e5e7eb] p-5 grid grid-cols-2 sm:grid-cols-4 gap-5">
                  <ScoreBandTile
                    label="Critical (< 55)"
                    count={bands.buckets.critical}
                    pct={bands.pct.critical}
                    color="#dc2626"
                    testId="band-critical"
                  />
                  <ScoreBandTile
                    label="Warning (55–74)"
                    count={bands.buckets.warning}
                    pct={bands.pct.warning}
                    color="#f59e0b"
                    testId="band-warning"
                  />
                  <ScoreBandTile
                    label="Strong (75–89)"
                    count={bands.buckets.strong}
                    pct={bands.pct.strong}
                    color="#002fa7"
                    testId="band-strong"
                  />
                  <ScoreBandTile
                    label="Elite (90+)"
                    count={bands.buckets.elite}
                    pct={bands.pct.elite}
                    color="#10b981"
                    testId="band-elite"
                  />
                </div>
              </section>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
};

export default AdminConversions;
